/**
 * PydanticAI Builder API Service
 *
 * Wraps the PydanticAI Builder endpoints (/api/ai/builder/*)
 * for use by the App Builder UI. Falls back to classic SiteBrain
 * routes when the PydanticAI endpoint is unavailable.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders(extra = {}) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
    ...extra,
  };
}

/**
 * Chat with the PydanticAI builder agent (SSE streaming).
 * Returns an EventSource-like interface that emits parsed events.
 */
export async function chatWithBuilder({
  message,
  projectId = null,
  model = 'gpt-4o',
  conversationHistory = [],
  onTextDelta,
  onComplete,
  onError,
  onClarifyingQuestions,
}) {
  const controller = new AbortController();

  try {
    const response = await fetch(`${API_URL}/api/ai/builder/chat`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        message,
        project_id: projectId,
        model,
        conversation_history: conversationHistory,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';

    const processStream = async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              onComplete?.(fullText);
              return;
            }
            try {
              const event = JSON.parse(data);
              if (event.type === 'text_delta') {
                fullText += event.content;
                onTextDelta?.(event.content, fullText);
              } else if (event.type === 'tool_call' && event.tool === 'ask_clarifying_questions') {
                try {
                  const toolResult = JSON.parse(event.content);
                  onClarifyingQuestions?.(toolResult.questions, toolResult.context);
                } catch (_) { /* ignore parse errors */ }
              } else if (event.type === 'complete') {
                onComplete?.(event.content, event.project_id);
              } else if (event.type === 'error') {
                onError?.(new Error(event.content));
              }
            } catch (e) {
              // Skip unparseable lines
            }
          }
        }
      }
      // Stream ended without [DONE]
      if (fullText) {
        onComplete?.(fullText);
      }
    };

    processStream().catch((err) => {
      if (err.name !== 'AbortError') {
        onError?.(err);
      }
    });
  } catch (err) {
    if (err.name !== 'AbortError') {
      onError?.(err);
    }
  }

  return { abort: () => controller.abort() };
}

/**
 * Create a project via PydanticAI.
 */
export async function createProject({ name, description, appType = 'adaptive', fullStack = true, model = 'gpt-4o' }) {
  const response = await fetch(`${API_URL}/api/ai/builder/create`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ name, description, app_type: appType, full_stack: fullStack, model }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to create project' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * List projects via PydanticAI.
 */
export async function listProjects() {
  const response = await fetch(`${API_URL}/api/ai/builder/projects`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get project status via PydanticAI.
 */
export async function getProject(projectId) {
  const response = await fetch(`${API_URL}/api/ai/builder/projects/${projectId}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Modify a project via PydanticAI.
 */
export async function modifyProject({ projectId, modification, autoApply = true, model = 'gpt-4o' }) {
  const response = await fetch(`${API_URL}/api/ai/builder/projects/${projectId}/modify`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ modification, auto_apply: autoApply, model }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to modify project' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Deploy a project via PydanticAI.
 */
export async function deployProject({ projectId, target = 'unified' }) {
  const response = await fetch(`${API_URL}/api/ai/builder/projects/${projectId}/deploy`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ target }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to deploy' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Check if PydanticAI builder is available.
 */
export async function isPydanticAiAvailable() {
  try {
    const response = await fetch(`${API_URL}/api/ai/builder/projects`, {
      headers: authHeaders(),
    });
    return response.ok || response.status === 401;
  } catch {
    return false;
  }
}
